import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toastify.css';
import Login from './components/Login/Login';
import ProjectSelectionPage from './components/ProjectSelection/ProjectSelectionPage';
import MainLayout from './components/MainLayout/MainLayout';
import PersonalDashboard from './components/Dashboard/PersonalDashboard';
import Loading from './components/Loading/Loading';
import useDeploymentRefresh from './hooks/useDeploymentRefresh';
import { isTelegramMiniApp as detectTelegramMiniApp } from './utils/telegram';
import { IS_UNIFIED_AUTH_CONFIGURED, redirectToUnifiedAuth } from './utils/unifiedAuth';
import BasketPolicyDialog from './components/Shared/BasketPolicyDialog';

const ChatWidget = lazy(() => import('./components/Chat/ChatWidget'));
const RepairRequestList = lazy(() => import('./components/RepairRequest/RepairRequestList'));
const RepairRequestDetails = lazy(() => import('./components/RepairRequest/RepairRequestDetails'));
const ReportConstructor = lazy(() => import('./components/ReportConstructor/ReportConstructor'));
const ReportPage = lazy(() => import('./components/ReportConstructor/ReportPage'));
const DiagnosticsPage = lazy(() => import('./components/Diagnostics/DiagnosticsPage'));
const DiagnosticsDetailPage = lazy(() => import('./components/Diagnostics/DiagnosticsDetailPage'));
const RepairPage = lazy(() => import('./components/Repair/RepairPage'));
const QualityControlPage = lazy(() => import('./components/QualityControl/QualityControlPage'));
const QualityControlDetailPage = lazy(() => import('./components/QualityControl/QualityControlDetailPage'));
const CleaningPage = lazy(() => import('./components/Cleaning/CleaningPage'));
const CleaningDetailPage = lazy(() => import('./components/Cleaning/CleaningDetailPage'));
const WaitingPartsPage = lazy(() => import('./components/WaitingParts/WaitingPartsPage'));
const PackagingPage = lazy(() => import('./components/Packaging/PackagingPage'));
const PackagingDetails = lazy(() => import('./components/Packaging/PackagingDetails'));
const ShippingPage = lazy(() => import('./components/Shipping/ShippingPage'));
const ShippedPage = lazy(() => import('./components/Shipping/ShippedPage'));
const ShippingDetails = lazy(() => import('./components/Shipping/ShippingDetails'));
const SerialDetailPage = lazy(() => import('./components/SerialSearch/SerialDetailPage'));
const EquipmentDetailPage = lazy(() => import('./components/Equipment/EquipmentDetailPage'));
const TrackingPage = lazy(() => import('./components/Tracking/TrackingPage'));
const BasketsPage = lazy(() => import('./components/Baskets/BasketsPage'));
const ApprovalPage = lazy(() => import('./components/Approval/ApprovalPage'));
const ApprovalDetailPage = lazy(() => import('./components/Approval/ApprovalDetailPage'));
const UploadCustomerFile = lazy(() => import('./components/Customer/UploadCustomerFile'));
const HistoryPage = lazy(() => import('./components/History/HistoryPage'));
const HistoryDetailPage = lazy(() => import('./components/History/HistoryDetailPage'));
const SettingsPage = lazy(() => import('./components/Settings/SettingsPage'));
const StatisticsPage = lazy(() => import('./components/Statistics/StatisticsPage'));
const EstimatesPage = lazy(() => import('./components/Estimates/EstimatesPage'));
const UserProfile = lazy(() => import('./components/Profile/UserProfile'));
const ClientPortal = lazy(() => import('./components/ClientPortal/ClientPortal'));

const UnifiedLoginRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    const from = location?.state?.from;
    const fromPath = from && from.pathname
      ? `${from.pathname}${from.search || ''}${from.hash || ''}`
      : `${location.pathname}${location.search}${location.hash || ''}`;

    const nextPath = fromPath === '/login' ? '/' : fromPath;
    redirectToUnifiedAuth(nextPath);
  }, [location]);

  return null;
};

const PrivateRoute = ({ children }) => {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const params = new URLSearchParams(location.search);
  const ssoSource = params.get('auth_source');
  const ssoAccessToken = params.get('access_token');
  const ssoRefreshToken = params.get('refresh_token');
  const ssoUserFullName = params.get('user_full_name');

  if (ssoSource === 'auth_service' && ssoAccessToken) {
    try {
      localStorage.setItem('access_token', ssoAccessToken);
      if (ssoRefreshToken) {
        localStorage.setItem('refresh_token', ssoRefreshToken);
      }
      if (ssoUserFullName) {
        localStorage.setItem('user_full_name', ssoUserFullName);
      }
    } catch {
      /* ignore */
    }

    params.delete('auth_source');
    params.delete('access_token');
    params.delete('refresh_token');
    params.delete('user_full_name');
    params.delete('sso_ts');

    const cleanedSearch = params.toString();
    const cleanedUrl = `${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${location.hash || ''}`;
    const currentUrl = `${location.pathname}${location.search}${location.hash || ''}`;
    if (cleanedUrl !== currentUrl) {
      window.history.replaceState(null, '', cleanedUrl);
    }
  }

  const isAuthenticated = localStorage.getItem('access_token') !== null;
  let selectedProjectId = localStorage.getItem('selected_project_id');
  const isTelegramMiniApp = detectTelegramMiniApp();
  const isApprovalsRoute = location.pathname.startsWith('/approvals');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (isTelegramMiniApp) {
        localStorage.setItem('telegram_miniapp', '1');
      } else {
        localStorage.removeItem('telegram_miniapp');
      }
    } catch {
      /* ignore */
    }
  }, [isTelegramMiniApp]);
  const projectParam = params.get('project');
  if (projectParam && projectParam !== selectedProjectId) {
    try {
      localStorage.setItem('selected_project_id', projectParam);
      selectedProjectId = projectParam;
    } catch {
      /* ignore */
    }
  }

  if (!isAuthenticated) {
    if (isTelegramMiniApp && isApprovalsRoute) {
      return children;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (authLoading) {
    return null;
  }

  const roleNames = (user?.roles || []).map(role => role.name);
  const isGuest = roleNames.includes('Гость');

  const allowedWithoutProject = ['/select-project', '/settings', '/statistics', '/estimates', '/profile', '/client'];
  const canSkipProject = allowedWithoutProject.some((path) => {
    if (path === '/profile') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  });

  const shouldBypassProjectCheck = canSkipProject || (isTelegramMiniApp && isApprovalsRoute);

  if (!selectedProjectId && !shouldBypassProjectCheck) {
    return <Navigate to="/select-project" state={{ from: location }} />;
  }

  if (
    isGuest
    && !location.pathname.startsWith('/client')
    && location.pathname !== '/select-project'
  ) {
    return <Navigate to={selectedProjectId ? '/client' : '/select-project'} replace />;
  }

  return children;
};

const ChatWidgetGate = () => {
  const { user } = useAuth();
  const roleNames = (user?.roles || []).map(role => role.name);
  if (roleNames.includes('Гость')) {
    return null;
  }
  return <Suspense fallback={null}><ChatWidget /></Suspense>;
};

function App() {
  useDeploymentRefresh();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const mediaQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

    const syncPerformanceMode = () => {
      const ua = navigator.userAgent || '';
      const isWindows = /\bWindows\b/i.test(ua);
      const isYandexBrowser = /YaBrowser/i.test(ua);
      const lowCoreCpu =
        typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;
      const lowMemoryDevice =
        typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
      const prefersReducedMotion = Boolean(mediaQuery?.matches);

      const shouldUsePerformanceMode =
        isWindows || isYandexBrowser || lowCoreCpu || lowMemoryDevice || prefersReducedMotion;

      document.body.classList.toggle('performance-mode', shouldUsePerformanceMode);
    };

    syncPerformanceMode();
    mediaQuery?.addEventListener?.('change', syncPerformanceMode);

    return () => {
      mediaQuery?.removeEventListener?.('change', syncPerformanceMode);
    };
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <ChatProvider>
          <Suspense fallback={<Loading fullPage message="Загрузка раздела..." />}>
          <Routes>
            <Route path="/login" element={IS_UNIFIED_AUTH_CONFIGURED ? <UnifiedLoginRedirect /> : <Login />} />
            <Route path="/select-project" element={<PrivateRoute><ProjectSelectionPage /></PrivateRoute>} />
            <Route path="/client" element={<PrivateRoute><ClientPortal /></PrivateRoute>} />
            
            <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
            
            <Route path="/statistics" element={<PrivateRoute><StatisticsPage /></PrivateRoute>} />
            
            <Route path="/estimates" element={<PrivateRoute><EstimatesPage /></PrivateRoute>} />
            
            <Route path="/" element={<PrivateRoute><MainLayout /></PrivateRoute>}>
              <Route index element={<PersonalDashboard />} />
              <Route path="repair-requests" element={<RepairRequestList />} />
              <Route path="repair-requests/:id" element={<RepairRequestDetails />} />
              <Route path="reports/constructor" element={<ReportConstructor />} />
              <Route path="reports" element={<ReportPage />} />
              <Route path="diagnostics" element={<DiagnosticsPage />} />
              <Route path="diagnostics/:id" element={<DiagnosticsDetailPage />} />
              <Route path="repair" element={<RepairPage />} />
              <Route path="quality-control" element={<QualityControlPage />} />
              <Route path="quality-control/:id" element={<QualityControlDetailPage />} />
              <Route path="cleaning" element={<CleaningPage />} />
              <Route path="cleaning/:id" element={<CleaningDetailPage />} />
              <Route path="waiting-parts" element={<WaitingPartsPage />} />
              <Route path="packaging" element={<PackagingPage />} />
              <Route path="packaging/details" element={<PackagingDetails />} />
              <Route path="shipping" element={<ShippingPage />} />
              <Route path="shipping/shipped" element={<ShippedPage />} />
              <Route path="shipping/details" element={<ShippingDetails />} />
              <Route path="serial/:serialNumber" element={<SerialDetailPage />} />
              <Route path="equipment/:id" element={<EquipmentDetailPage />} />
              <Route path="tracking" element={<TrackingPage />} />
              <Route path="baskets" element={<BasketsPage />} />
              <Route path="approvals" element={<ApprovalPage />} />
              <Route path="approvals/:repairRequestId" element={<ApprovalDetailPage />} />
              <Route path="customers/upload" element={<UploadCustomerFile />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="history/:equipmentId" element={<HistoryDetailPage />} />
              <Route path="profile" element={<UserProfile />} />
            
            </Route>
          </Routes>
          </Suspense>
          <ToastContainer
            position="bottom-left"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            closeButton
            rtl={false}
            pauseOnFocusLoss={false}
            draggable={false}
            pauseOnHover={false}
            theme="light"
          />
          <BasketPolicyDialog />
          <ChatWidgetGate />
        </ChatProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
